import { useEffect, useRef, useState } from "react";
import { parseProgramProgress, type ProgramProgress } from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { ProgressByProgram } from "./usePrograms";

export type UseEditedProgramProgressOptions = {
  apiFetch: ProgramApiFetch;
  /** The host's cache of the markers the Plan surface last read (mobile
   *  AsyncStorage, web localStorage). Absent: the server only. */
  readCached?: () => Promise<ProgressByProgram | null>;
};

// Where the caller is in the program the editor opens (#336 slice 5): its
// executed rounds are locked. The cached marker first, so it holds offline,
// then the server's, for any program, followed or not. No program (a new
// one): null, nothing locked. A failed or null fetch keeps the cached marker. Lifted
// from the mobile screen for slice 6: the hosts inject only the reads.
export function useEditedProgramProgress(
  programId: string | undefined,
  opts: UseEditedProgramProgressOptions,
): ProgramProgress | null {
  // Tagged with its program: a hook that outlives the editor session (web) must
  // never hand program A's marker to B, not even for one render.
  const [stored, setStored] = useState<{ id: string; progress: ProgramProgress } | null>(null);
  // Hosts may pass fresh literals per render: the effect reads the latest.
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });
  useEffect(() => {
    if (!programId) return;
    let cancelled = false;
    void (async () => {
      const { apiFetch, readCached } = optsRef.current;
      const cached = (await readCached?.())?.[programId] ?? null;
      if (!cancelled && cached) setStored({ id: programId, progress: cached });
      try {
        const raw = await apiFetch<unknown>(`/api/me/programs/${encodeURIComponent(programId)}/progress`);
        // A null (never run, or a payload that drifted from the schema) must
        // not wipe the cached marker: it would unlock the rounds already done.
        const fresh = parseProgramProgress(raw);
        if (!cancelled && fresh) setStored({ id: programId, progress: fresh });
      } catch {
        // Offline or refused: the cached marker stands.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [programId]);
  return programId && stored?.id === programId ? stored.progress : null;
}
